/**
 * Pure formatting and ordering for iManage items: file kinds, sizes, dates,
 * client/matter labels, sorting, and splitting a listing into folders,
 * documents and emails.
 *
 * @module components/apps/imanage/imanageFormat
 */
import type { IManageEntry } from "@amoreg/contracts/imanage";

export type IManageSortKey = "name" | "version" | "author" | "modified" | "size";
export type SortDirection = "asc" | "desc";

export type FileKind =
  | "folder"
  | "workspace"
  | "word"
  | "pdf"
  | "email"
  | "sheet"
  | "slides"
  | "text"
  | "image"
  | "archive"
  | "other";

const KIND_BY_EXTENSION: Readonly<Record<string, FileKind>> = {
  doc: "word",
  docx: "word",
  docm: "word",
  dot: "word",
  dotx: "word",
  rtf: "word",
  odt: "word",
  pdf: "pdf",
  eml: "email",
  msg: "email",
  xls: "sheet",
  xlsx: "sheet",
  xlsm: "sheet",
  csv: "sheet",
  ods: "sheet",
  ppt: "slides",
  pptx: "slides",
  odp: "slides",
  txt: "text",
  md: "text",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  tif: "image",
  tiff: "image",
  zip: "archive",
};

export function fileKind(entry: Pick<IManageEntry, "kind" | "extension" | "email">): FileKind {
  if (entry.kind === "folder") return "folder";
  if (entry.kind === "matter" || entry.kind === "library") return "workspace";
  if (entry.email) return "email";
  return (entry.extension && KIND_BY_EXTENSION[entry.extension.toLowerCase()]) || "other";
}

/** Short type label for the table and details ("Word", "PDF", "Email"). */
export function fileKindLabel(entry: Pick<IManageEntry, "kind" | "extension" | "email">): string {
  const kind = fileKind(entry);
  const labels: Record<FileKind, string> = {
    folder: "Folder",
    workspace: "Workspace",
    word: "Word",
    pdf: "PDF",
    email: "Email",
    sheet: "Spreadsheet",
    slides: "Presentation",
    text: "Text",
    image: "Image",
    archive: "Archive",
    other: entry.extension ? entry.extension.toUpperCase() : "File",
  };
  return labels[kind];
}

export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1).replace(/\.0$/, "") : Math.round(value)} ${units[unit]}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Compact, Linear-style time: "Just now", "12m", "3h", "Yesterday", "Oct 2",
 * and "Oct 2025" beyond the current year. UTC-free: local calendar days.
 */
export function formatWhen(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "—";
  const diff = now - time;
  if (diff < 60_000 && diff > -60_000) return "Just now";
  if (diff > 0 && diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  const date = new Date(time);
  const today = new Date(now);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  if (time >= startOfToday && diff > 0) return `${Math.floor(diff / 3_600_000)}h`;
  if (time >= startOfToday - 86_400_000 && time < startOfToday) return "Yesterday";
  if (date.getFullYear() === today.getFullYear())
    return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Full local date and time, for details and tooltips. */
export function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "—";
  const date = new Date(time);
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${hh}:${mm}`;
}

/** "1042-001" from a workspace's client and matter numbers. */
export function clientMatter(entry: Pick<IManageEntry, "client" | "matter">): string | null {
  if (!entry.client) return null;
  return entry.matter ? `${entry.client}-${entry.matter}` : entry.client;
}

/** "Helen Marsh" from "Helen Marsh <helen@example.test>". */
export function senderName(from: string | undefined): string | undefined {
  if (!from) return undefined;
  const named = /^\s*"?([^"<]+?)"?\s*</.exec(from);
  return (named ? named[1]! : from).trim();
}

/** Who a row names in the Author column: an email's sender, else the author. */
export const rowAuthor = (entry: IManageEntry): string | undefined =>
  entry.email ? (senderName(entry.emailFrom) ?? entry.author) : entry.author;

/** When a row happened: an email's sent time, else its last edit. */
export const rowTime = (entry: IManageEntry): string | undefined =>
  entry.email ? (entry.sentAt ?? entry.modified) : entry.modified;

/** A file name to save as: the name with its extension, once. */
export function downloadName(entry: Pick<IManageEntry, "name" | "extension">): string {
  const safe = entry.name.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ").trim() || "document";
  return entry.extension && !safe.toLowerCase().endsWith(`.${entry.extension.toLowerCase()}`)
    ? `${safe}.${entry.extension}`
    : safe;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/**
 * Sorted copy. Folders stay above documents whatever the key, as in iManage
 * and every file browser. Missing values sort last in both directions.
 */
export function sortEntries(
  entries: readonly IManageEntry[],
  key: IManageSortKey,
  direction: SortDirection,
): IManageEntry[] {
  const sign = direction === "asc" ? 1 : -1;
  const value = (entry: IManageEntry): string | number | undefined => {
    switch (key) {
      case "name":
        return entry.name;
      case "version":
        return entry.version ?? undefined;
      case "author":
        return rowAuthor(entry);
      case "modified": {
        const time = Date.parse(rowTime(entry) ?? "");
        return Number.isFinite(time) ? time : undefined;
      }
      case "size":
        return entry.size;
    }
  };
  return [...entries].sort((a, b) => {
    const folderA = a.kind === "folder" ? 0 : 1;
    const folderB = b.kind === "folder" ? 0 : 1;
    if (folderA !== folderB) return folderA - folderB;
    const x = value(a);
    const y = value(b);
    if (x === undefined && y === undefined) return collator.compare(a.name, b.name);
    if (x === undefined) return 1;
    if (y === undefined) return -1;
    const order =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : collator.compare(String(x), String(y));
    return order !== 0 ? order * sign : collator.compare(a.name, b.name);
  });
}

/** The natural direction when a column is first chosen: newest/largest first. */
export const defaultDirection = (key: IManageSortKey): SortDirection =>
  key === "name" || key === "author" ? "asc" : "desc";

export function partitionEntries(entries: readonly IManageEntry[]) {
  const folders: IManageEntry[] = [];
  const documents: IManageEntry[] = [];
  const emails: IManageEntry[] = [];
  for (const entry of entries) {
    if (entry.kind === "folder" || entry.kind === "matter") folders.push(entry);
    else if (entry.kind === "document") (entry.email ? emails : documents).push(entry);
  }
  return { folders, documents, emails };
}

/**
 * The display names of an address header ("Ana <a@x.test>, Tom <t@x.test>"
 * reads "Ana, Tom"): each `<…>` address and the space before it is dropped.
 * A linear scan rather than a tag-stripping regex — the result is plain text.
 */
export function recipientNames(value: string): string {
  let out = "";
  let cursor = 0;
  while (cursor < value.length) {
    const open = value.indexOf("<", cursor);
    const close = open === -1 ? -1 : value.indexOf(">", open);
    if (close === -1) return out + value.slice(cursor);
    out += value.slice(cursor, open).trimEnd();
    cursor = close + 1;
  }
  return out;
}
